import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {container} from "tsyringe";
import type {RichTextDocument} from "@o2d/rich-text-editor";
import {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {RichTextDocumentHelper} from "../../../helpers/rich-text-document.helper";

// Side-effect import: registers <o2d-rich-text-editor>. This popup does NOT
// go through knowledge-rte.component.ts (deliberately — same reasoning as
// ha-dialog-whiteboard-view: that wrapper bakes in an Edit/Save toolbar and
// pasted-image upload, all editing machinery this readonly popup must NOT
// have), so it must not depend on that component having been loaded first.
import "@o2d/rich-text-editor";

/**
 * Readonly popup viewer for a `KnowledgeObject` of type `document` (RTE) —
 * opened by a Linkview tile's `_open()` (`link-tile-knowledge-object.
 * component.ts`), the RTE-document analog of `ha-dialog-whiteboard-view`
 * (Anke, 2026-09-05: "die rte-dokumente aus dem whiteboard-mode als link
 * hinterlegt werden können... alles andere genauso wie beim richtigen
 * whiteboard"). Same generation-counter load pattern, same try/catch +
 * notify + close-on-error/not-found, same `DialogHelper` close mechanics as
 * that sibling — see its own doc comment for the shared reasoning, not
 * repeated here.
 *
 * One real difference from the whiteboard popup: `<o2d-rich-text-editor>`
 * has no boolean `readonly` — instead a `mode` property that already
 * DEFAULTS to `'view'` (never mounts an editable tiptap `Editor`, per its
 * own doc comment); set explicitly here anyway for the same "this must never
 * accidentally become editable" clarity the whiteboard popup's `readonly`
 * attribute gives. No toolbar to hide (the package's own formatting toolbar
 * — `.o2d-rte-toolbar` — only ever renders in `mode="edit"`), no Transformer-
 * equivalent to suppress, no pointer-events/click-to-select guard needed at
 * all — plain read-only text has no click-to-select concept to begin with.
 * The one click that does something here: an image opens the enlarged preview
 * (`rich-text-image-click` → `ImagePreviewListener`, 2026-09-26); it lies over
 * this dialog and closes with it.
 */
@customElement('ha-dialog-rte-view')
export class HaDialogRteViewComponent extends AbstractDialogComponent {

    @property({type: String})
    public uid = '';

    @state()
    private _content: RichTextDocument | null = null;

    @state()
    private _isReady = false;

    /** Monotonic counter guarding against a stale `findOneByIdWithContent`
     *  resolving after `uid` has already changed — `DialogHelper` reuses the
     *  SAME dialog instance while one of this type is already open
     *  (`Object.assign(dialog, payload)` + `.open()`), so a second tile click
     *  can change `uid` mid-request. Mirrors `ha-dialog-whiteboard-view`'s
     *  identical guard. */
    private _loadGeneration = 0;

    public constructor() {
        super();
        this.closable = true;
    }

    /**
     * `protected`, as Lit declares it — the tests reach it through a small
     * subclass that exposes it (see the spec's `TestableDialog`), rather than
     * this widening its visibility for their convenience.
     */
    protected async willUpdate(changedProperties: PropertyValues): Promise<void> {
        if (!changedProperties.has('uid') || !this.uid) {
            return;
        }

        this._loadGeneration += 1;
        const myGeneration = this._loadGeneration;
        this._isReady = false;

        let knowledgeObject;
        try {
            knowledgeObject = await container.resolve(KnowledgeObjectRepository).findOneByIdWithContent(this.uid);
        } catch (error) {
            if (myGeneration !== this._loadGeneration || !this.isConnected) return;
            console.error('Failed to load document for the readonly popup', error);
            UiHelper.notify({
                level: 'error',
                title: 'Fehler beim Laden',
                content: html`<p>Das Dokument konnte nicht geladen werden.</p>`,
                closable: true,
            });
            this._close();
            return;
        }
        if (myGeneration !== this._loadGeneration || !this.isConnected) {
            // A newer load started, or the dialog was closed while this was
            // in flight — discard; acting now could resurrect an already-
            // closed popup or overwrite a newer uid's content.
            return;
        }

        // `findOneByIdWithContent` itself does NOT filter on `deletedAt`
        // (unlike `findAll`) — the protection here comes entirely from the
        // backend's own default soft-delete filter. Mirrors
        // ha-dialog-whiteboard-view's identical documented assumption.
        if (!knowledgeObject || knowledgeObject.type !== 'document') {
            UiHelper.notify({
                level: 'error',
                title: 'Nicht gefunden',
                content: html`<p>Das verknüpfte Dokument existiert nicht mehr.</p>`,
                closable: true,
            });
            this._close();
            return;
        }

        this.title = knowledgeObject.title;
        // LOAD-side cast + guard (mirrors knowledge-rte.component.ts's
        // identical concern): the entity types `content.payload` as the
        // whiteboard-era `unknown[]`, but a `document` persists a single
        // ProseMirror-JSON object. A freshly-created object seeds
        // `content.payload = []` (the backend create handler uses an empty
        // array for ALL types) — not a valid RichTextDocument — treated as
        // absent so the viewer renders an empty document rather than
        // choking on an array.
        const rawPayload = knowledgeObject.content?.payload as unknown;
        this._content = RichTextDocumentHelper.isRichTextDocument(rawPayload) ? rawPayload : null;
        this._isReady = true;
    }

    protected _renderMainContent(): HTMLTemplateResult {
        if (!this._isReady) {
            return html`<div class="ha-dialog-rte-view__loading"></div>`;
        }

        return html`
            <o2d-rich-text-editor .mode=${'view'} .content=${this._content ?? undefined}></o2d-rich-text-editor>
        `;
    }

    protected onClickClose(): void {
        DialogHelper.closeDialog(this.tagName);
        super.onClickClose();
    }

    private _close(): void {
        DialogHelper.closeDialog(this.tagName);
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0,
        });
    }

}
